import React from 'react';

/**
 * Navigation glyphs — solid, mass-based, matching Direction C.
 *
 * The design preview's nav used filled geometry (`fill="currentColor"`, rounded
 * rectangles, no strokes), in the same language as the N monogram. The sidebar
 * never moved off Lucide's thin outline set, so the shell's densest cluster of
 * icons was the one place still carrying the old treatment.
 *
 * Rules for anything added here:
 *   - 24x24 viewBox, `fill="currentColor"`, never a stroke
 *   - built from rects and simple paths, corners rounded ~1.5–2
 *   - readable at 20px, which is the size the sidebar renders
 */

type GlyphProps = { className?: string };

const Svg: React.FC<GlyphProps & { children: React.ReactNode }> = ({ className, children }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    {children}
  </svg>
);

/** Dashboard — four solid tiles. */
export const NavDashboard: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <rect x="3" y="3" width="8" height="8" rx="2" />
    <rect x="13" y="3" width="8" height="8" rx="2" />
    <rect x="3" y="13" width="8" height="8" rx="2" />
    <rect x="13" y="13" width="8" height="8" rx="2" />
  </Svg>
);

/** Restaurants / menu — a cup with a handle. */
export const NavCoffee: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M4 8h12v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8Z" />
    <path d="M17 9.5h1.5a2.75 2.75 0 0 1 0 5.5H17v-2h1.5a.75.75 0 0 0 0-1.5H17v-2Z" />
    <rect x="4" y="20.5" width="14" height="2" rx="1" />
  </Svg>
);

/** Access control — a solid shield. */
export const NavShield: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M12 2.5 20 5.5v6c0 5-3.4 9.2-8 10.5-4.6-1.3-8-5.5-8-10.5v-6l8-3Z" />
  </Svg>
);

/** Analytics — ascending bars. */
export const NavTrending: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <rect x="3" y="13" width="4.5" height="8" rx="1.5" />
    <rect x="9.75" y="8" width="4.5" height="13" rx="1.5" />
    <rect x="16.5" y="3" width="4.5" height="18" rx="1.5" />
  </Svg>
);

/** Settings — a chunky cog. */
export const NavSettings: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M10.2 2h3.6l.5 2.7 2 1.15 2.6-.9 1.8 3.1-2.1 1.8v2.3l2.1 1.8-1.8 3.1-2.6-.9-2 1.15-.5 2.7h-3.6l-.5-2.7-2-1.15-2.6.9-1.8-3.1 2.1-1.8v-2.3L3.3 8.05l1.8-3.1 2.6.9 2-1.15L10.2 2Zm1.8 6.4a3.6 3.6 0 1 0 0 7.2 3.6 3.6 0 0 0 0-7.2Z" />
  </Svg>
);

/** Kitchen monitor — a screen on a stand. */
export const NavMonitor: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <rect x="2.5" y="3.5" width="19" height="12.5" rx="2.5" />
    <rect x="10.5" y="17" width="3" height="2.5" />
    <rect x="7" y="19.5" width="10" height="2" rx="1" />
  </Svg>
);

/** People. */
export const NavUsers: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <circle cx="9" cy="7.5" r="3.75" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0 1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1Z" />
    <circle cx="17.5" cy="8.5" r="2.75" />
    <path d="M17 13.5a4.75 4.75 0 0 1 4.5 5 .9.9 0 0 1-.9.9H17.9c.07-2.2-.4-4.3-1.5-5.9h.6Z" />
  </Svg>
);

/** Tables — a plan view. */
export const NavTables: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <rect x="2.5" y="4" width="19" height="16" rx="2.5" />
    <rect x="4.5" y="9.5" width="15" height="1.8" fill="#fff" fillOpacity="0.35" />
    <rect x="11.1" y="6" width="1.8" height="12" fill="#fff" fillOpacity="0.35" />
  </Svg>
);

/** Orders — a stacked list. */
export const NavOrders: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <rect x="3" y="4" width="4" height="4" rx="1.25" />
    <rect x="3" y="10" width="4" height="4" rx="1.25" />
    <rect x="3" y="16" width="4" height="4" rx="1.25" />
    <rect x="9" y="5" width="12" height="2.5" rx="1.25" />
    <rect x="9" y="11" width="12" height="2.5" rx="1.25" />
    <rect x="9" y="17" width="8.5" height="2.5" rx="1.25" />
  </Svg>
);

/** Kitchen — a chef's toque. */
export const NavChef: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M12 2.5a5 5 0 0 1 4.6 3.05A4.75 4.75 0 0 1 18.5 14.5v.5h-13v-.5A4.75 4.75 0 0 1 7.4 5.55 5 5 0 0 1 12 2.5Z" />
    <rect x="5.5" y="16.5" width="13" height="5" rx="1.5" />
  </Svg>
);

/** Staff management — a person with a cog. */
export const NavStaff: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <circle cx="10" cy="7.5" r="4" />
    <path d="M2.5 20.5A7.5 7.5 0 0 1 14 14.1a6.6 6.6 0 0 0-1.2 6.4H3.5a1 1 0 0 1-1-1Z" />
    <circle cx="18" cy="17.5" r="4.25" />
  </Svg>
);

/** Reports — a filled pie. */
export const NavPie: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M11 2.6v9.4h9.4A9.5 9.5 0 0 1 11 21.5 9.5 9.5 0 0 1 11 2.6Z" />
    <path d="M13 2.6a9.5 9.5 0 0 1 8.4 8.4H13V2.6Z" />
  </Svg>
);

/** Performance — a target. */
export const NavTarget: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 3.5a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13Z" />
    <circle cx="12" cy="12" r="3.25" />
  </Svg>
);

/** Menu / receipt. */
export const NavReceipt: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M4.5 2.5h15v19l-2.5-1.6-2.5 1.6-2.5-1.6-2.5 1.6-2.5-1.6L4.5 21.5v-19Z" />
    <rect x="7.5" y="7" width="9" height="2" rx="1" fill="#fff" fillOpacity="0.4" />
    <rect x="7.5" y="11.5" width="9" height="2" rx="1" fill="#fff" fillOpacity="0.4" />
  </Svg>
);

/** POS / payments — a card. */
export const NavCard: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <rect x="2" y="4.5" width="20" height="15" rx="2.5" />
    <rect x="2" y="8.5" width="20" height="3" fill="#fff" fillOpacity="0.4" />
    <rect x="5" y="14.5" width="5.5" height="2" rx="1" fill="#fff" fillOpacity="0.4" />
  </Svg>
);

/** Approvals — a person with a tick. */
export const NavUserCheck: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <circle cx="9.5" cy="7.5" r="4" />
    <path d="M2 20.5A7.5 7.5 0 0 1 14.6 15a1 1 0 0 0-.1.15l-2.9 4.2-.6-.6a1 1 0 0 0-1.5 1.3l.5.45H3a1 1 0 0 1-1-1Z" />
    <path d="m14.4 19.6 5.9-7.1a1.6 1.6 0 0 1 2.5 2l-7 8.4a1.6 1.6 0 0 1-2.3.1l-2.6-2.4a1.6 1.6 0 0 1 2.2-2.3l1.3 1.3Z" />
  </Svg>
);

/* ── shell chrome ─────────────────────────────────────────────────────────
 * The header's hamburger and search, and the sidebar's log-out and close,
 * sit alongside the nav and were the last outline glyphs left in the shell.
 */

/** Hamburger — three bars, same rhythm as the orders list. */
export const NavMenu: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="2.75" rx="1.375" />
    <rect x="3" y="10.6" width="18" height="2.75" rx="1.375" />
    <rect x="3" y="16.2" width="18" height="2.75" rx="1.375" />
  </Svg>
);

/** Search — solid lens and handle. */
export const NavSearch: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M10.5 2a8.5 8.5 0 1 0 5.06 15.34l4.05 4.05a1.5 1.5 0 0 0 2.12-2.12l-4.05-4.05A8.5 8.5 0 0 0 10.5 2Zm0 3a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11Z" />
  </Svg>
);

/** Close. */
export const NavClose: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M6.4 4.3 12 9.9l5.6-5.6a1.5 1.5 0 0 1 2.1 2.1L14.1 12l5.6 5.6a1.5 1.5 0 0 1-2.1 2.1L12 14.1l-5.6 5.6a1.5 1.5 0 0 1-2.1-2.1L9.9 12 4.3 6.4a1.5 1.5 0 0 1 2.1-2.1Z" />
  </Svg>
);

/** Log out — door with an outbound arrow. */
export const NavLogout: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M4 3.5h7a1.5 1.5 0 0 1 0 3H5.5v11H11a1.5 1.5 0 0 1 0 3H4a1.5 1.5 0 0 1-1.5-1.5V5A1.5 1.5 0 0 1 4 3.5Z" />
    <path d="m15.4 7.4 4.1 4.1a1.5 1.5 0 0 1 0 2.1l-4.1 4.1a1.5 1.5 0 0 1-2.1-2.1l1.5-1.55H9a1.5 1.5 0 0 1 0-3h5.8L13.3 9.5a1.5 1.5 0 0 1 2.1-2.1Z" />
  </Svg>
);

/** Language switcher — a globe with meridians. */
export const NavLanguages: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm6.9 7h-2.6a15.3 15.3 0 0 0-1.5-4.4A8 8 0 0 1 18.9 9ZM12 4.2c.8 1.1 1.5 2.7 1.9 4.8h-3.8c.4-2.1 1.1-3.7 1.9-4.8ZM4.3 13a8 8 0 0 1 0-2h3a19 19 0 0 0 0 2h-3Zm.8 2h2.6c.3 1.6.8 3.1 1.5 4.4A8 8 0 0 1 5.1 15Zm2.6-6H5.1a8 8 0 0 1 4.1-4.4A15.3 15.3 0 0 0 7.7 9ZM12 19.8c-.8-1.1-1.5-2.7-1.9-4.8h3.8c-.4 2.1-1.1 3.7-1.9 4.8ZM14.3 13H9.7a17 17 0 0 1 0-2h4.6a17 17 0 0 1 0 2Zm.5 6.4c.7-1.3 1.2-2.8 1.5-4.4h2.6a8 8 0 0 1-4.1 4.4ZM16.7 13a19 19 0 0 0 0-2h3a8 8 0 0 1 0 2h-3Z" />
  </Svg>
);

/** Notifications — a solid bell. */
export const NavBell: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M12 2a6.5 6.5 0 0 0-6.5 6.5c0 3.2-.7 5.2-1.4 6.4A1.4 1.4 0 0 0 5.3 17h13.4a1.4 1.4 0 0 0 1.2-2.1c-.7-1.2-1.4-3.2-1.4-6.4A6.5 6.5 0 0 0 12 2Z" />
    <path d="M9.3 18.5h5.4a2.75 2.75 0 0 1-5.4 0Z" />
  </Svg>
);

/* ── action + object glyphs ───────────────────────────────────────────────
 * Used on dashboard tiles, empty states and row actions, so a screen does not
 * mix filled nav icons with outline ones.
 */

/** A building — restaurants as places. */
export const NavBuilding: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M4 2.5h10a1.5 1.5 0 0 1 1.5 1.5v17.5h-13V4A1.5 1.5 0 0 1 4 2.5Z" />
    <rect x="17" y="9" width="5" height="12.5" rx="1.5" />
    <rect x="6" y="5.5" width="2.5" height="2.5" rx="0.6" fill="#fff" fillOpacity="0.45" />
    <rect x="10" y="5.5" width="2.5" height="2.5" rx="0.6" fill="#fff" fillOpacity="0.45" />
    <rect x="6" y="10" width="2.5" height="2.5" rx="0.6" fill="#fff" fillOpacity="0.45" />
    <rect x="10" y="10" width="2.5" height="2.5" rx="0.6" fill="#fff" fillOpacity="0.45" />
    <rect x="7.5" y="15.5" width="3.5" height="6" rx="0.8" fill="#fff" fillOpacity="0.45" />
  </Svg>
);

/** Add. */
export const NavPlus: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M10.4 3.5h3.2v6.9h6.9v3.2h-6.9v6.9h-3.2v-6.9H3.5v-3.2h6.9V3.5Z" />
  </Svg>
);

/** View. */
export const NavEye: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M12 4.5c5 0 9.1 3.1 10.8 7.5-1.7 4.4-5.8 7.5-10.8 7.5S2.9 16.4 1.2 12C2.9 7.6 7 4.5 12 4.5Zm0 3.25a4.25 4.25 0 1 0 0 8.5 4.25 4.25 0 0 0 0-8.5Z" />
    <circle cx="12" cy="12" r="2" fill="#fff" fillOpacity="0.5" />
  </Svg>
);

/** Edit. */
export const NavEdit: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M14.9 4.3 19.7 9.1 9.4 19.4l-5.5.7.7-5.5L14.9 4.3Z" />
    <path d="M16.6 2.6 18 1.2a1.8 1.8 0 0 1 2.5 0l2.3 2.3a1.8 1.8 0 0 1 0 2.5l-1.4 1.4-4.8-4.8Z" />
  </Svg>
);

/* ── contact, status and row actions ──────────────────────────────────────
 * The partner rows on the platform dashboard: contact details, status marks
 * and the per-row menu.
 */

/**
 * Login.
 *
 * Replaced NavMail, which was the only glyph in this set that no longer has
 * anything to label: accounts are identified by a `name@restaurantCode` login
 * and the platform stores no email address at all. An envelope beside a login
 * would say "write to this", which is the one thing it is not for.
 */
export const NavAtSign: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M12 2a10 10 0 1 0 5.4 18.4 1 1 0 1 0-1.08-1.68A8 8 0 1 1 20 12v1a1.6 1.6 0 0 1-3.2 0V8.2a1 1 0 0 0-2 0v.35A4.6 4.6 0 1 0 12 16.6a4.57 4.57 0 0 0 3.2-1.3 3.6 3.6 0 0 0 6.8-1.6v-1.7A10 10 0 0 0 12 2Zm0 12.6a2.6 2.6 0 1 1 0-5.2 2.6 2.6 0 0 1 0 5.2Z" />
  </Svg>
);

/** Phone. */
export const NavPhone: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M7.4 2.6a1.7 1.7 0 0 1 2.3.5l2 3a1.7 1.7 0 0 1-.3 2.2l-1.5 1.3a12 12 0 0 0 4.5 4.5l1.3-1.5a1.7 1.7 0 0 1 2.2-.3l3 2a1.7 1.7 0 0 1 .5 2.3l-1.3 2a2.6 2.6 0 0 1-3 1.05C11.1 17.9 6.1 12.9 3.65 6.9a2.6 2.6 0 0 1 1.05-3l2.7-1.3Z" />
  </Svg>
);

/** Location. */
export const NavPin: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M12 2a7.5 7.5 0 0 0-7.5 7.5c0 5.2 6.1 11.4 6.8 12.1a1 1 0 0 0 1.4 0c.7-.7 6.8-6.9 6.8-12.1A7.5 7.5 0 0 0 12 2Zm0 5a2.75 2.75 0 1 1 0 5.5A2.75 2.75 0 0 1 12 7Z" />
  </Svg>
);

/** Date. */
export const NavCalendar: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <rect x="2.5" y="4.5" width="19" height="17" rx="2.5" />
    <rect x="6.5" y="1.5" width="2.5" height="5" rx="1.25" />
    <rect x="15" y="1.5" width="2.5" height="5" rx="1.25" />
    <rect x="2.5" y="9" width="19" height="1.8" fill="#fff" fillOpacity="0.45" />
    <rect x="6" y="13" width="3" height="3" rx="0.8" fill="#fff" fillOpacity="0.45" />
    <rect x="11" y="13" width="3" height="3" rx="0.8" fill="#fff" fillOpacity="0.45" />
  </Svg>
);

/** Confirmed / active. */
export const NavCheck: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm5 7.4-6.1 7.3a1.4 1.4 0 0 1-2 .15l-3.1-2.8a1.4 1.4 0 1 1 1.9-2.1l2 1.8 5.1-6.15a1.4 1.4 0 0 1 2.2 1.8Z" />
  </Svg>
);

/** Row menu. */
export const NavMore: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="5" r="2.1" />
    <circle cx="12" cy="12" r="2.1" />
    <circle cx="12" cy="19" r="2.1" />
  </Svg>
);

/** Suspend / lock. */
export const NavLock: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M12 1.5A5.25 5.25 0 0 0 6.75 6.75V9.5h2.9V6.75a2.35 2.35 0 0 1 4.7 0V9.5h2.9V6.75A5.25 5.25 0 0 0 12 1.5Z" />
    <rect x="4" y="9.5" width="16" height="13" rx="2.5" />
    <circle cx="12" cy="15" r="1.9" fill="#fff" fillOpacity="0.5" />
  </Svg>
);

/** Delete. */
export const NavTrash: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M9.5 1.5h5a1.5 1.5 0 0 1 1.5 1.5v1h4.5a1.25 1.25 0 0 1 0 2.5H3.5a1.25 1.25 0 0 1 0-2.5H8V3a1.5 1.5 0 0 1 1.5-1.5Z" />
    <path d="M5 8.5h14l-.9 12a2 2 0 0 1-2 1.9H7.9a2 2 0 0 1-2-1.9L5 8.5Z" />
  </Svg>
);

/** Trend. */
export const NavTrend: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M3.6 16.9 9 11.5l3.5 3.5 6-6H15V6.5h7V13h-2.5v-2.2l-7 7L9 14.3l-3.6 3.6-1.8-1Z" />
  </Svg>
);

/** Warning / attention. */
export const NavAlert: React.FC<GlyphProps> = (p) => (
  <Svg {...p}>
    <path d="M13.3 3.1a1.5 1.5 0 0 0-2.6 0L1.4 19.4A1.5 1.5 0 0 0 2.7 21.7h18.6a1.5 1.5 0 0 0 1.3-2.3L13.3 3.1ZM12 8.2c.7 0 1.3.6 1.25 1.3l-.3 4.6a.95.95 0 0 1-1.9 0l-.3-4.6c-.05-.7.55-1.3 1.25-1.3Zm0 8a1.35 1.35 0 1 1 0 2.7 1.35 1.35 0 0 1 0-2.7Z" />
  </Svg>
);
